import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  LayoutGrid,
  Users,
  Target,
  Briefcase,
  FileText,
  FileSignature,
  Mail,
  Calendar,
  CheckSquare,
  Receipt,
  Coins,
  Wallet,
  Landmark,
  Boxes,
  Scale,
  Brain,
  Calculator,
  BookOpen,
  BookCheck,
  ShieldCheck,
  TrendingUp,
  Sparkles,
  ScanText,
  ChevronDown,
  UserCircle,
  Users2,
  Package,
  Handshake,
  Building2,
  Send,
  Plug,
  RotateCw,
  Megaphone,
  type LucideIcon,
} from 'lucide-react'
import { useTheme } from '../theme/ThemeContext'
import { useAuth } from '../contexts/AuthContext'
import { useTranslation } from 'react-i18next'

const COLLAPSED_KEY = 'jr2os.sidebar.collapsed'

function slugifyGroup(label: string) {
  return label
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function readCollapsedState(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(COLLAPSED_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === 'object') return parsed as Record<string, boolean>
    return {}
  } catch {
    return {}
  }
}

function writeCollapsedState(state: Record<string, boolean>) {
  try {
    localStorage.setItem(COLLAPSED_KEY, JSON.stringify(state))
  } catch {
    // ignora (quota / private mode)
  }
}

import {
  filterGroupsForRole,
  isNavParent,
  type Accent,
  type NavEntry,
  type NavItemDef,
  type NavParentDef,
} from './navItems'


interface AccentDef {
  iconDark: string
  iconLight: string
  bgDark: string
  bgLight: string
  textDark: string
  textLight: string
  bar: string
  glow: string
}

const accentMap: Record<Accent, AccentDef> = {
  jr2: {
    iconDark: 'text-jr2-300',
    iconLight: 'text-jr2-700',
    bgDark: 'bg-jr2-500/15',
    bgLight: 'bg-jr2-500/10',
    textDark: 'text-jr2-700 dark:text-jr2-200',
    textLight: 'text-jr2-800',
    bar: 'from-jr2-300 to-jr2-500',
    glow: 'rgba(27,79,216,0.55)',
  },
  cyan: {
    iconDark: 'text-cyan-300',
    iconLight: 'text-cyan-600',
    bgDark: 'bg-cyan-500/15',
    bgLight: 'bg-cyan-500/10',
    textDark: 'text-cyan-700 dark:text-cyan-200',
    textLight: 'text-cyan-700',
    bar: 'from-cyan-300 to-cyan-500',
    glow: 'rgba(34,211,238,0.55)',
  },
  purple: {
    iconDark: 'text-purple-300',
    iconLight: 'text-purple-600',
    bgDark: 'bg-purple-500/15',
    bgLight: 'bg-purple-500/10',
    textDark: 'text-purple-700 dark:text-purple-200',
    textLight: 'text-purple-700',
    bar: 'from-purple-300 to-purple-500',
    glow: 'rgba(168,85,247,0.55)',
  },
  pink: {
    iconDark: 'text-pink-300',
    iconLight: 'text-pink-600',
    bgDark: 'bg-pink-500/15',
    bgLight: 'bg-pink-500/10',
    textDark: 'text-pink-700 dark:text-pink-200',
    textLight: 'text-pink-700',
    bar: 'from-pink-300 to-pink-500',
    glow: 'rgba(244,114,182,0.55)',
  },
  orange: {
    iconDark: 'text-orange-300',
    iconLight: 'text-orange-600',
    bgDark: 'bg-orange-500/15',
    bgLight: 'bg-orange-500/10',
    textDark: 'text-orange-700 dark:text-orange-200',
    textLight: 'text-orange-700',
    bar: 'from-orange-300 to-orange-500',
    glow: 'rgba(251,146,60,0.55)',
  },
  rose: {
    iconDark: 'text-rose-300',
    iconLight: 'text-rose-600',
    bgDark: 'bg-rose-500/15',
    bgLight: 'bg-rose-500/10',
    textDark: 'text-rose-700 dark:text-rose-200',
    textLight: 'text-rose-700',
    bar: 'from-rose-300 to-rose-500',
    glow: 'rgba(244,63,94,0.55)',
  },
  emerald: {
    iconDark: 'text-emerald-300',
    iconLight: 'text-emerald-600',
    bgDark: 'bg-emerald-500/15',
    bgLight: 'bg-emerald-500/10',
    textDark: 'text-emerald-700 dark:text-emerald-200',
    textLight: 'text-emerald-700',
    bar: 'from-emerald-300 to-emerald-500',
    glow: 'rgba(52,211,153,0.55)',
  },
  indigo: {
    iconDark: 'text-indigo-300',
    iconLight: 'text-indigo-600',
    bgDark: 'bg-indigo-500/15',
    bgLight: 'bg-indigo-500/10',
    textDark: 'text-indigo-700 dark:text-indigo-200',
    textLight: 'text-indigo-700',
    bar: 'from-indigo-300 to-indigo-500',
    glow: 'rgba(129,140,248,0.55)',
  },
  amber: {
    iconDark: 'text-amber-300',
    iconLight: 'text-amber-600',
    bgDark: 'bg-amber-500/15',
    bgLight: 'bg-amber-500/10',
    textDark: 'text-amber-700 dark:text-amber-200',
    textLight: 'text-amber-700',
    bar: 'from-amber-300 to-amber-500',
    glow: 'rgba(251,191,36,0.55)',
  },
  sky: {
    iconDark: 'text-sky-300',
    iconLight: 'text-sky-600',
    bgDark: 'bg-sky-500/15',
    bgLight: 'bg-sky-500/10',
    textDark: 'text-sky-700 dark:text-sky-200',
    textLight: 'text-sky-700',
    bar: 'from-sky-300 to-sky-500',
    glow: 'rgba(56,189,248,0.55)',
  },
  blue: {
    iconDark: 'text-blue-300',
    iconLight: 'text-blue-600',
    bgDark: 'bg-blue-500/15',
    bgLight: 'bg-blue-500/10',
    textDark: 'text-blue-700 dark:text-blue-200',
    textLight: 'text-blue-700',
    bar: 'from-blue-300 to-blue-500',
    glow: 'rgba(96,165,250,0.55)',
  },
  fuchsia: {
    iconDark: 'text-fuchsia-300',
    iconLight: 'text-fuchsia-600',
    bgDark: 'bg-fuchsia-500/15',
    bgLight: 'bg-fuchsia-500/10',
    textDark: 'text-fuchsia-200',
    textLight: 'text-fuchsia-700',
    bar: 'from-fuchsia-300 to-fuchsia-500',
    glow: 'rgba(232,121,249,0.55)',
  },
}

interface NavItemProps extends NavItemDef {
  isActive: boolean
  isLight: boolean
}

function NavItem({ to, label, labelKey, icon: Icon, accent, isActive, isLight, external, child }: NavItemProps) {
  const { t } = useTranslation('common')
  const a = accentMap[accent]
  const activeBg = isLight ? `${a.bgLight} ${a.textLight}` : `${a.bgDark} ${a.textDark}`
  const idleColor = isLight
    ? 't-secondary hover:text-slate-900 hover:bg-slate-200/60'
    : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
  const iconColor = isActive
    ? isLight
      ? a.iconLight
      : a.iconDark
    : isLight
      ? 't-muted group-hover:text-slate-700'
      : 'text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200'

  // La indentación es SOLO tipografía: sangrado, texto e icono más pequeños.
  // No añade un nivel plegable ni un clic — es lo que hace que una sección de
  // trece filas se lea como seis bloques.
  const className = [
    'group relative flex items-center rounded-lg font-medium transition-all',
    child ? 'gap-2 pl-9 pr-3 py-1.5 text-[13px]' : 'gap-3 pl-4 pr-3 py-2 text-sm',
    isActive ? activeBg : idleColor,
  ].join(' ')
  const style = isActive ? { boxShadow: `0 0 22px ${a.glow}` } : undefined
  const inner = (
    <>
      {isActive && (
        <span
          className={`absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r bg-gradient-to-b ${a.bar} shadow-[0_0_10px_rgba(255,255,255,0.4)]`}
        />
      )}
      <Icon size={child ? 15 : 18} className={iconColor} />
      <span>{t(labelKey, label)}</span>
    </>
  )

  // Enlace externo real (SSO a JR2 MKT): navegación del navegador al backend,
  // NO interceptada por react-router. Se abre en pestaña nueva.
  if (external) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" className={className} style={style}>
        {inner}
      </a>
    )
  }

  return (
    <Link to={to} className={className} style={style}>
      {inner}
    </Link>
  )
}

interface NavParentProps {
  entry: NavParentDef
  open: boolean
  onToggle: () => void
  /** Alguno de sus hijos es la pantalla en la que estoy. */
  hasActiveChild: boolean
  isItemActive: (to: string) => boolean
  isLight: boolean
}

/**
 * Carpeta plegable del menú («Estados financieros»). A diferencia de la sangría de `child` en
 * un NavItem —que es solo tipografía—, esta SÍ pliega. No navega: pulsarla abre o cierra. Se abre
 * sola cuando la URL es la de un hijo (lo decide el Sidebar), y plegada con un hijo activo
 * conserva el resaltado, para no perder «dónde estoy».
 */
function NavParent({ entry, open, onToggle, hasActiveChild, isItemActive, isLight }: NavParentProps) {
  const { t } = useTranslation('common')
  const a = accentMap[entry.accent]
  const Icon = entry.icon
  const highlighted = hasActiveChild && !open
  const activeBg = isLight ? `${a.bgLight} ${a.textLight}` : `${a.bgDark} ${a.textDark}`
  const idleColor = isLight
    ? 't-secondary hover:text-slate-900 hover:bg-slate-200/60'
    : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
  const iconColor = highlighted
    ? isLight
      ? a.iconLight
      : a.iconDark
    : isLight
      ? 't-muted group-hover:text-slate-700'
      : 'text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200'
  const regionId = `sidebar-parent-region-${entry.id}`

  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={regionId}
        onClick={onToggle}
        className={[
          'group relative flex w-full items-center gap-3 rounded-lg pl-4 pr-3 py-2 text-left text-sm font-medium transition-all',
          highlighted ? activeBg : idleColor,
        ].join(' ')}
      >
        <Icon size={18} className={iconColor} />
        <span className="flex-1">{t(entry.labelKey, entry.label)}</span>
        <ChevronDown
          size={14}
          className={`t-muted transition-transform ${open ? '' : '-rotate-90'}`}
        />
      </button>
      {/* Plegada = no se renderiza: un enlace oculto con max-h-0 sigue siendo enfocable con el
          teclado. */}
      {open && (
        <div id={regionId} className="mt-1 flex flex-col gap-1">
          {entry.children.map((it) => (
            <NavItem key={it.to} {...it} child isActive={isItemActive(it.to)} isLight={isLight} />
          ))}
        </div>
      )}
    </div>
  )
}

interface SidebarProps {
  isOpen?: boolean
  onClose?: () => void
}

export default function Sidebar({ isOpen = false, onClose }: SidebarProps = {}) {
  const { t } = useTranslation('common')
  const { theme } = useTheme()
  const isLight = theme === 'light'
  const { pathname } = useLocation()
  const auth = useAuth()
  const isItemActive = (to: string) => pathname === to || pathname.startsWith(to + '/')

  // La regla de visibilidad por rol vive en navItems.ts (la comparte el buscador ⌘K).
  const visibleGroups = filterGroupsForRole(auth.status === 'authenticated' ? auth.user.role : null)

  const isEntryActive = (e: NavEntry): boolean =>
    isNavParent(e) ? e.children.some((c) => isItemActive(c.to)) : isItemActive(e.to)

  // Carpetas abiertas. NO se persisten: las abre la URL (si estás en Balance, «Estados
  // financieros» está abierta) y las demás las deja como las dejes hasta recargar.
  const [openParents, setOpenParents] = useState<Set<string>>(() => {
    const initial = new Set<string>()
    for (const g of visibleGroups) {
      for (const e of g.items) if (isNavParent(e) && isEntryActive(e)) initial.add(e.id)
    }
    return initial
  })
  useEffect(() => {
    setOpenParents((prev) => {
      let next: Set<string> | null = null
      for (const g of visibleGroups) {
        for (const e of g.items) {
          if (isNavParent(e) && isEntryActive(e) && !prev.has(e.id)) {
            next ??= new Set(prev)
            next.add(e.id)
          }
        }
      }
      return next ?? prev
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])
  const toggleParent = (id: string) =>
    setOpenParents((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  // Sidebar tipo acordeón: PRINCIPAL siempre visible, y como mucho UN
  // grupo adicional abierto. Click en un grupo cierra el que estuviera
  // antes y abre el nuevo (reclick lo colapsa).
  // Persistimos solo el "openGroup" actual (string | null) en localStorage.
  const PRIMARY_GROUP = 'PRINCIPAL'
  const [openGroup, setOpenGroup] = useState<string | null>(() => {
    const stored = readCollapsedState()
    const raw = (stored as Record<string, unknown>).__openGroup
    return typeof raw === 'string' ? raw : null
  })

  // Persistencia: guardamos un objeto {__openGroup: 'GESTIÓN'} para
  // mantener compat con readCollapsedState (devuelve Record<string, boolean>).
  useEffect(() => {
    writeCollapsedState({ __openGroup: openGroup } as unknown as Record<string, boolean>)
  }, [openGroup])

  // Auto-expand del grupo que contiene la ruta activa.
  useEffect(() => {
    const groupWithActive = visibleGroups.find((g) => g.items.some(isEntryActive))
    if (!groupWithActive) return
    if (groupWithActive.label === PRIMARY_GROUP) return // ya está siempre abierto
    setOpenGroup((prev) => (prev === groupWithActive.label ? prev : groupWithActive.label))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  const toggleGroup = (label: string) => {
    if (label === PRIMARY_GROUP) return // PRINCIPAL no se colapsa
    setOpenGroup((prev) => (prev === label ? null : label))
  }
  const isGroupExpanded = (label: string) =>
    label === PRIMARY_GROUP ? true : openGroup === label

  return (
    <>
      {/* Overlay click-to-close, solo móvil */}
      {isOpen && (
        <button
          type="button"
          aria-label={t('sidebar.closeMenu')}
          onClick={onClose}
          className="md:hidden fixed inset-0 z-30 bg-black/60 backdrop-blur-sm"
        />
      )}
    <aside
      className={[
        // En escritorio: columna fija; en móvil: drawer fijo a la izquierda
        'z-40 w-52 shrink-0 h-screen flex flex-col backdrop-blur-md border-r transition-transform duration-300',
        'fixed top-0 left-0 md:sticky md:top-0',
        isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
      ].join(' ')}
      style={{ background: 'var(--bg-sidebar)', borderColor: 'var(--border)' }}
    >
      {/* Marca */}
      <div className="px-5 pt-6 pb-5">
        <Link to="/dashboard" className="flex items-center gap-2 group" aria-label="JR2 OS">
          <img
            src={isLight ? '/logo-mark.svg' : '/logo-mark-light.svg'}
            alt="JR2"
            draggable={false}
            className="h-7 w-auto select-none"
          />
          <span className="os-badge">OS</span>
        </Link>
      </div>

      {/* Grupos de navegación */}
      <nav className="flex-1 overflow-y-auto px-3 pb-4">
        {visibleGroups.map((g) => {
          const expanded = isGroupExpanded(g.label)
          const isPrimary = g.label === PRIMARY_GROUP
          const slug = slugifyGroup(g.label)
          const headerId = `sidebar-group-header-${slug}`
          const regionId = `sidebar-group-region-${slug}`
          return (
            <div key={g.label} className="mb-5">
              <button
                type="button"
                id={headerId}
                aria-expanded={expanded}
                aria-controls={regionId}
                onClick={() => toggleGroup(g.label)}
                disabled={isPrimary}
                className={[
                  'w-full flex items-center justify-between px-3 mb-2 text-[11px] font-semibold tracking-[0.18em] rounded-md py-1 transition-colors',
                  isPrimary ? 'cursor-default' : 'cursor-pointer',
                  isLight
                    ? 'text-slate-400 hover:text-slate-600 hover:bg-slate-200/40'
                    : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/40',
                ].join(' ')}
              >
                <span>{t(g.labelKey, g.label)}</span>
                {!isPrimary && (
                  <ChevronDown
                    size={12}
                    className={`t-muted transition-transform ${expanded ? '' : '-rotate-90'}`}
                  />
                )}
              </button>
              <div
                id={regionId}
                role="region"
                aria-labelledby={headerId}
                className={[
                  'overflow-hidden transition-[max-height] duration-200 ease-out',
                  expanded ? 'max-h-[800px]' : 'max-h-0',
                ].join(' ')}
              >
                <div className="flex flex-col gap-1">
                  {g.items.map((it) =>
                    isNavParent(it) ? (
                      <NavParent
                        key={it.id}
                        entry={it}
                        open={openParents.has(it.id)}
                        onToggle={() => toggleParent(it.id)}
                        hasActiveChild={isEntryActive(it)}
                        isItemActive={isItemActive}
                        isLight={isLight}
                      />
                    ) : (
                      <NavItem key={it.to} {...it} isActive={isItemActive(it.to)} isLight={isLight} />
                    )
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </nav>

      {/* Pie — M7: Ajustes vive ahora como grupo del menú; aquí solo el badge. */}
      <div
        className="px-3 pb-5 pt-3 border-t flex flex-col gap-1"
        style={{ borderColor: 'var(--border)' }}
      >
        <VersionBadge isLight={isLight} />
      </div>
    </aside>
    </>
  )
}

function VersionBadge({ isLight }: { isLight: boolean }) {
  // Formato: v0.1.0 · abcd1234 · 2026-07-01. Tooltip con timestamp completo.
  const builtDate = __APP_BUILT_AT__.slice(0, 10)
  const tooltip = `Versión ${__APP_VERSION__}\nCommit ${__APP_GIT_SHA__}\nBuild ${__APP_BUILT_AT__}`
  return (
    <div
      title={tooltip}
      className={[
        'select-text text-[10px] leading-tight px-3 pt-2 mt-1 border-t font-mono',
        isLight ? 'text-slate-400 border-slate-200/60' : 'text-slate-500 border-slate-800/60',
      ].join(' ')}
      style={{ borderColor: 'var(--border)' }}
    >
      <div>
        v{__APP_VERSION__} · {__APP_GIT_SHA__}
      </div>
      <div className="opacity-70">{builtDate}</div>
    </div>
  )
}
